// ---------------------------------------------------------------------------
// AgentForm.utils — pure helpers that translate between the form-state row
// model (AgentForm.types) and the API agent shapes (types/agent). No React.
// ---------------------------------------------------------------------------

import type {
  Agent,
  UpsertAgentBody,
  VariableDeclaration,
  VariableType,
} from '../../../types/agent.js';
import { isLlmSpec } from '../../../types/agent.js';
import type {
  BareTemplateToken as GenericBareTemplateToken,
  NamedText,
} from '../../../utils/templateRefs.js';
import {
  collectBareTemplateTokens as collectBareTemplateTokensFromFields,
  collectReferencedVars as collectReferencedVarsFromFields,
  replaceBareTokenWithVar,
} from '../../../utils/templateRefs.js';

import type { AgentFormState, VariableRow } from './AgentForm.types.js';

let rowSeq = 0;

/** Stable, unique client-side id for a form row. */
export function makeRowId(prefix: string): string {
  rowSeq += 1;
  return `${prefix}-${String(rowSeq)}`;
}

export function emptyVariableRow(): VariableRow {
  return {
    id: makeRowId('var'),
    name: '',
    type: 'String',
    defaultValue: '',
    description: '',
  };
}

export function emptyFormState(): AgentFormState {
  return {
    name: '',
    displayName: '',
    description: '',
    modelName: '',
    systemPrompt: '',
    temperature: '',
    topP: '',
    maxTokens: '',
    contextWindow: '',
    includeConversationHistory: '',
    userMessage: '',
    variables: [],
  };
}

function defaultToInput(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

/** Build the editable form state from an existing agent (edit/view modes). */
export function seedFormFromAgent(agent: Agent): AgentFormState {
  const state = emptyFormState();
  state.name = agent.name;
  state.displayName = agent.displayName ?? '';
  state.description = agent.description ?? '';

  if (isLlmSpec(agent.spec)) {
    const { init, invoke } = agent.spec;
    // Coalesce to '' — although the spec types mark these required, snapshot
    // payloads (and adapter specs) can carry null/undefined here, and the form
    // fields treat them as controlled strings (reading `.length` downstream).
    state.modelName = init.modelName ?? '';
    state.systemPrompt = init.systemPrompt ?? '';
    state.temperature = init.temperature ?? '';
    state.topP = init.topP ?? '';
    state.maxTokens = init.maxTokens ?? '';
    state.contextWindow = init.contextWindow ?? '';
    state.includeConversationHistory = init.includeConversationHistory ?? '';
    state.userMessage = invoke.userMessage ?? '';
  }

  if (agent.variables) {
    state.variables = Object.entries(agent.variables).map(([name, decl]) => ({
      id: makeRowId('var'),
      name,
      type: decl.type,
      defaultValue: defaultToInput(decl.default),
      description: decl.description ?? '',
    }));
  }

  return state;
}

export function isVariableRowEmpty(row: VariableRow): boolean {
  return (
    row.name.trim() === '' &&
    row.defaultValue.trim() === '' &&
    row.description.trim() === ''
  );
}

/**
 * The set of variable names actually declared by the form: non-empty rows whose
 * trimmed name is non-blank. Single source of truth for "what counts as
 * declared", shared by validation (undeclared-`$var` detection) and the upsert
 * body builder.
 */
export function collectDeclaredVarNames(rows: VariableRow[]): Set<string> {
  const names = new Set<string>();
  for (const row of rows) {
    if (isVariableRowEmpty(row)) continue;
    const name = row.name.trim();
    if (name !== '') names.add(name);
  }
  return names;
}

/**
 * Coerce a variable default (held as text in the form) to the runtime type the
 * variable declares. Numeric/boolean values that don't parse cleanly (e.g. a
 * `${VAR}` substitution token) are preserved as the raw string.
 */
export function coerceVariableDefault(
  type: VariableType,
  raw: string,
): unknown {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  switch (type) {
    case 'Number': {
      const n = Number(trimmed);
      return Number.isFinite(n) ? n : trimmed;
    }
    case 'Integer': {
      const n = Number(trimmed);
      return Number.isInteger(n) ? n : trimmed;
    }
    case 'Boolean': {
      if (trimmed === 'true') return true;
      if (trimmed === 'false') return false;
      return trimmed;
    }
    case 'String':
      return raw;
  }
}

function trimmedOrUndefined(value: string): string | undefined {
  const t = value.trim();
  return t.length > 0 ? t : undefined;
}

export function variableRowsToMap(
  rows: VariableRow[],
): Record<string, VariableDeclaration> {
  const map: Record<string, VariableDeclaration> = {};
  for (const row of rows) {
    if (isVariableRowEmpty(row)) continue;
    const name = row.name.trim();
    if (name === '') continue;
    const decl: VariableDeclaration = { type: row.type };
    const def = coerceVariableDefault(row.type, row.defaultValue);
    if (def !== undefined) decl.default = def;
    const description = trimmedOrUndefined(row.description);
    if (description !== undefined) decl.description = description;
    map[name] = decl;
  }
  return map;
}

// ---------------------------------------------------------------------------
// Template-reference detection — the agent's spec-bearing text fields may embed
// `{{$var:NAME}}` (a declared-variable reference) or `{{$expr:...}}` (a runtime
// query/data expression). Bare `{{NAME}}` is neither. The detection itself is
// resource-agnostic and lives in `utils/templateRefs`; here we adapt it to the
// agent's form-state fields so the create/edit form can force every `$var` to
// be declared and warn on bare tokens.
// ---------------------------------------------------------------------------

/** Spec-bearing string fields whose text may carry template references. */
export const SPEC_TEMPLATE_FIELDS = [
  'modelName',
  'systemPrompt',
  'temperature',
  'topP',
  'maxTokens',
  'contextWindow',
  'includeConversationHistory',
  'userMessage',
] as const satisfies readonly (keyof AgentFormState)[];

export type SpecTemplateField = (typeof SPEC_TEMPLATE_FIELDS)[number];

/** A bare `{{...}}` token in one of the agent's spec fields. */
export type BareTemplateToken = GenericBareTemplateToken<SpecTemplateField>;

export { replaceBareTokenWithVar };

/** The agent's spec text fields as `{ field, text }` pairs for scanning. */
function agentTemplateFields(
  state: AgentFormState,
): NamedText<SpecTemplateField>[] {
  return SPEC_TEMPLATE_FIELDS.map((field) => ({ field, text: state[field] }));
}

/** Distinct `$var` names referenced across the agent's spec text fields. */
export function collectReferencedVars(state: AgentFormState): string[] {
  return collectReferencedVarsFromFields(agentTemplateFields(state));
}

/** Bare `{{...}}` tokens (not `$var:`/`$expr:`), de-duplicated per field. */
export function collectBareTemplateTokens(
  state: AgentFormState,
): BareTemplateToken[] {
  return collectBareTemplateTokensFromFields(agentTemplateFields(state));
}

/**
 * Assemble the PUT body from the current form state. Empty optional fields are
 * omitted; the variables map is omitted entirely when empty so the request
 * stays minimal.
 *
 * Metadata is system-managed and never edited in the form. When replacing an
 * existing agent (PUT replaces the whole working copy), pass the agent's
 * current `metadata` as `preservedMetadata` so it is carried through unchanged.
 */
export function buildUpsertBody(
  state: AgentFormState,
  preservedMetadata?: Record<string, string>,
): UpsertAgentBody {
  const body: UpsertAgentBody = {
    spec: {
      agentType: 'AgentLlmSpec',
      init: {
        modelName: state.modelName.trim(),
        systemPrompt: state.systemPrompt,
        ...(trimmedOrUndefined(state.temperature) !== undefined && {
          temperature: state.temperature.trim(),
        }),
        ...(trimmedOrUndefined(state.topP) !== undefined && {
          topP: state.topP.trim(),
        }),
        ...(trimmedOrUndefined(state.maxTokens) !== undefined && {
          maxTokens: state.maxTokens.trim(),
        }),
        ...(trimmedOrUndefined(state.contextWindow) !== undefined && {
          contextWindow: state.contextWindow.trim(),
        }),
        ...(trimmedOrUndefined(state.includeConversationHistory) !==
          undefined && {
          includeConversationHistory: state.includeConversationHistory.trim(),
        }),
      },
      invoke: { userMessage: state.userMessage },
    },
  };

  const displayName = trimmedOrUndefined(state.displayName);
  if (displayName !== undefined) body.displayName = displayName;

  const description = trimmedOrUndefined(state.description);
  if (description !== undefined) body.description = description;

  const variables = variableRowsToMap(state.variables);
  if (Object.keys(variables).length > 0) body.variables = variables;

  if (
    preservedMetadata !== undefined &&
    Object.keys(preservedMetadata).length > 0
  ) {
    body.metadata = preservedMetadata;
  }

  return body;
}
